// src/components/global-content/ContentList.js - Content listing and filtering component
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ContentCard from './ContentCard';

const ContentList = ({
  content,
  filters,
  pagination,
  loading,
  error,
  contentTypes,
  methodologies,
  onFilterChange,
  onPageChange,
  onEditContent,
  onArchiveContent,
  onFetchContent,
  formatDate,
  getContentTypeInfo
}) => {
  const [sortField, setSortField] = useState(filters.orderBy);
  const [sortDirection, setSortDirection] = useState(filters.orderDirection);

  const handleSearch = (e) => {
    onFilterChange({ search: e.target.value });
  };

  const handleContentTypeFilter = (e) => {
    onFilterChange({ contentType: e.target.value });
  };

  const handleSort = (field) => {
    const newDirection = sortField === field && sortDirection === 'ASC' ? 'DESC' : 'ASC';
    setSortField(field);
    setSortDirection(newDirection);
    onFilterChange({ orderBy: field, orderDirection: newDirection });
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
        </svg>
      );
    }
    return sortDirection === 'ASC' ? (
      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  return (
    <div className="content-list">
      {/* Active Filters */}
      <div className="content-filters card mb-6">
        <div className="filter-row">
          <div className="filter-group">
            <label className="form-label mb-2">Methodology</label>
            <select
              value={filters.methodology}
              onChange={(e) => onFilterChange({ methodology: e.target.value })}
              className="form-input"
              style={{ minWidth: '130px' }}
            >
              {methodologies.map(m => (
                <option key={m.id} value={m.id}>{m.displayName}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label className="form-label mb-2">Content Type</label>
            <select
              value={filters.contentType}
              onChange={handleContentTypeFilter}
              className="form-input"
              style={{ minWidth: '150px' }}
            >
              <option value="all">All Types</option>
              {contentTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div className="search-group">
            <label className="form-label mb-2">Search</label>
            <input
              type="text"
              placeholder="Search title, description, or content..."
              value={filters.search}
              onChange={handleSearch}
              className="form-input w-full"
            />
          </div>
        </div>
      </div>

      {/* Legacy Filters (commented out) */}
      {/* <div className="content-filters card mb-6">
        <div className="filter-row">
          <div className="filter-group">
            <label className="form-label mb-2">Content Type</label>
            <select 
              value={filters.contentType}
              onChange={handleContentTypeFilter}
              className="form-input"
              style={{ minWidth: '150px' }}
            >
              <option value="all">All Types</option>
              {contentTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div className="search-group">
            <label className="form-label mb-2">Search</label>
            <input
              type="text"
              placeholder="Search title, description, or content..."
              value={filters.search}
              onChange={handleSearch}
              className="form-input w-full"
            />
          </div>

          <div className="filter-group">
            <label className="form-label mb-2">Items per page</label>
            <select 
              value={filters.limit}
              onChange={(e) => onFilterChange({ limit: parseInt(e.target.value) })}
              className="form-input"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div> */}

        {/* Results Summary */}
        {/* {pagination.total > 0 && (
          <div className="filter-summary mt-4 pt-4 border-t border-gray-200">
            <div className="flex flex-between text-sm text-gray-600">
              <span>
                Showing {Math.min((filters.page - 1) * filters.limit + 1, pagination.total)} - {Math.min(filters.page * filters.limit, pagination.total)} of {pagination.total} items
              </span>
              <div className="flex gap-4">
                {filters.contentType !== 'all' && (
                  <span>Type: <strong>{getContentTypeInfo(filters.contentType).label}</strong></span>
                )}
                {filters.search && (
                  <span>Search: <strong>&ldquo;{filters.search}&rdquo;</strong></span>
                )}
              </div>
            </div>
          </div>
        )}
      </div> */}

      {/* Content List */}
      {loading ? (
        <div className="text-center py-8">
          <div className="loading-dots mx-auto mb-3">
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
          </div>
          <p className="text-gray-600">Loading content...</p>
        </div>
      ) : error ? (
        <div className="alert alert-error">
          <div>
            <strong>Error loading content:</strong> {error}
          </div>
        </div>
      ) : content.length === 0 ? (
        <div className="card text-center py-12">
          <div className="icon-container gray">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-700 mb-2">
            No content found
          </h3>
          <p className="text-gray-500 mb-4">
            {filters.search || filters.contentType !== 'all' 
              ? 'Try adjusting your filters or search terms.'
              : 'Get started by creating your first content item.'
            }
          </p>
        </div>
      ) : (
        <>
          {/* Sort Headers (Desktop) */}
          {/* <div className="content-list-headers hidden md:grid">
            <button 
              className="sort-header"
              onClick={() => handleSort('title')}
            >
              <span>Title</span>
              {getSortIcon('title')}
            </button>
            <button 
              className="sort-header"
              onClick={() => handleSort('content_type')}
            >
              <span>Type</span>
              {getSortIcon('content_type')}
            </button>
            <button 
              className="sort-header"
              onClick={() => handleSort('updated_at')}
            >
              <span>Last Updated</span>
              {getSortIcon('updated_at')}
            </button>
            <div className="sort-header-static">Actions</div>
          </div> */}

          {/* Content Cards */}
          <div className="space-y-4">
            {content.map(item => (
              <ContentCard
                key={item.id}
                content={item}
                onEdit={() => onEditContent(item)}
                onArchive={() => onArchiveContent(item)}
                onFetchContent={(key) => onFetchContent(key, item.methodologyId)}
                formatDate={formatDate}
                getContentTypeInfo={getContentTypeInfo}
              />
            ))}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="pagination mt-8">
              <button
                onClick={() => onPageChange(filters.page - 1)}
                disabled={filters.page <= 1}
                className="btn btn-secondary"
              >
                Previous
              </button>
              
              <div className="pagination-info">
                <span className="text-sm text-gray-600">
                  Page {filters.page} of {pagination.totalPages}
                </span>
              </div>
              
              <button
                onClick={() => onPageChange(filters.page + 1)}
                disabled={!pagination.hasMore}
                className="btn btn-secondary"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

ContentList.propTypes = {
  content: PropTypes.arrayOf(PropTypes.object).isRequired,
  filters: PropTypes.object.isRequired,
  pagination: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  contentTypes: PropTypes.arrayOf(PropTypes.object).isRequired,
  methodologies: PropTypes.arrayOf(PropTypes.object).isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onEditContent: PropTypes.func.isRequired,
  onArchiveContent: PropTypes.func.isRequired,
  onFetchContent: PropTypes.func.isRequired,
  formatDate: PropTypes.func.isRequired,
  getContentTypeInfo: PropTypes.func.isRequired
};

export default ContentList;